/**
 * Home feed — the recipe list for the active cookbook (Steg 1b), simplified in
 * issue #3: a toolbar of "＋ Ny oppskrift" (a sheet with every way to create a
 * recipe, incl. the imports) + the instant search, then a one-line filter row
 * ("Filter" button + active-filter chips) opening a filter sheet.
 *
 * Search and filtering are CLIENT-SIDE over the active cookbook's list (title /
 * intro keyword, category, tags AND, "Til gjennomgang"). The server stays
 * authoritative for visibility: the feed list only contains recipes the caller
 * may see.
 */

import { roleCanEditRecipes, type RecipeImportDraft, type RecipeSummary } from "@tjommi/shared";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { AddToCalendarDialog } from "../components/AddToCalendarDialog";
import { FilterSheet } from "../components/FilterSheet";
import { MealPlannerPanel, type MealPlannerPanelHandle } from "../components/MealPlannerPanel";
import { NewRecipeSheet } from "../components/NewRecipeSheet";
import { ShoppingListOverlay } from "../components/ShoppingListOverlay";
import { RecipeCard } from "../components/RecipeCard";
import { useCookbook } from "../cookbook/CookbookContext";
import { UnauthorizedError, fetchRecipes, markRecipeReviewed } from "../api";
import {
  dateFromDayDropId,
  pointerWithinOrClosestCenter,
  resolveMealPlanDrop,
  snapDragPreviewToCursor,
  type MealPlanDropRecipe,
} from "../lib/mealPlanDrag";
import { weekdayLabel } from "../lib/week";

/**
 * Norwegian screen-reader copy for the feed→planner drag (Ukeplanlegger,
 * Slice 4) — same idiom as `SectionsEditor`'s `A11Y` object. Module-level
 * (not a component) since it only ever reads data off the dnd-kit event
 * itself (the dragged recipe's title from `active.data.current`, the
 * hovered/dropped day's date from the `over` id), no component state needed.
 * Exported so the announcement wording can be unit-tested directly against
 * fake events — jsdom can't drive a real dnd-kit drag to observe these live.
 */
export const PLAN_DRAG_A11Y: {
  screenReaderInstructions: { draggable: string };
  announcements: Announcements;
} = {
  screenReaderInstructions: {
    draggable:
      "Trykk mellomrom eller enter for å løfte oppskriften. Bruk piltastene for å flytte den " +
      "over en dag i ukeplanen, mellomrom eller enter for å slippe, Escape for å avbryte.",
  },
  announcements: {
    onDragStart({ active }) {
      const recipe = (active.data.current as { recipe?: MealPlanDropRecipe } | undefined)?.recipe;
      return recipe
        ? `Løftet ${recipe.title}. Bruk piltastene for å flytte over en dag i ukeplanen.`
        : "Løftet oppskriften.";
    },
    onDragOver({ over }) {
      const date = over ? dateFromDayDropId(String(over.id)) : null;
      return date ? `Over ${weekdayLabel(date)}.` : "Flytter oppskriften.";
    },
    onDragEnd({ active, over }) {
      const recipe = (active.data.current as { recipe?: MealPlanDropRecipe } | undefined)?.recipe;
      const date = over ? dateFromDayDropId(String(over.id)) : null;
      if (recipe && date) return `Lagt til ${recipe.title} på ${weekdayLabel(date)}.`;
      return "Slapp oppskriften utenfor ukeplanen.";
    },
    onDragCancel() {
      return "Avbrøt planleggingen.";
    },
  },
};

/** The `DragOverlay` preview shown while a recipe card is being dragged
 * toward the planner — a small floating chip, not the full card. */
function RecipeDragPreview({ recipe }: { recipe: MealPlanDropRecipe }): React.JSX.Element {
  return (
    <div className="recipe-drag-preview" data-testid="recipe-drag-preview">
      {recipe.heroImageUrl && <img src={recipe.heroImageUrl} alt="" />}
      <span>{recipe.title}</span>
    </div>
  );
}

type FeedState =
  | { status: "loading" }
  | { status: "ok"; recipes: RecipeSummary[] }
  | { status: "unauthorized" }
  | { status: "error"; message: string };

const SearchIcon = (): React.JSX.Element => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const FilterIcon = (): React.JSX.Element => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M4 7h16M7 12h10M10 17h4" />
  </svg>
);

/** An active-filter chip next to the "Filter" button; ✕ removes that filter. */
function ActiveChip({
  label,
  testId,
  onRemove,
}: {
  label: string;
  testId: string;
  onRemove: () => void;
}): React.JSX.Element {
  return (
    <span className="active-chip" data-testid={testId}>
      {label}
      <button
        type="button"
        className="active-chip-x"
        aria-label={`Fjern filter ${label}`}
        data-testid={`${testId}-remove`}
        onClick={onRemove}
      >
        ✕
      </button>
    </span>
  );
}

const BookIcon = (): React.JSX.Element => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5z" />
    <path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20" />
  </svg>
);

const NoMatchIcon = (): React.JSX.Element => (
  <svg
    width="26"
    height="26"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
    <path d="M8.5 11h5" />
  </svg>
);

/** A designed empty state (icon + copy + optional action) — distinct from the
 * error blocks, so "nothing here yet" reads as intentional, not broken. */
function EmptyState({
  testId,
  icon,
  title,
  message,
  action,
}: {
  testId: string;
  icon: React.ReactNode;
  title: string;
  message: string;
  action?: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="feed-state feed-empty-state" data-testid={testId}>
      <span className="feed-empty-ic" aria-hidden="true">
        {icon}
      </span>
      <h2>{title}</h2>
      <p>{message}</p>
      {action}
    </div>
  );
}

export function HomePage({
  accessToken,
  userId,
  onSessionExpired,
}: {
  accessToken: string;
  /** The caller's subject — threaded to `MealPlannerPanel` to scope its
   *  remembered open/closed preference per user/device. */
  userId?: string;
  onSessionExpired: () => void;
}): React.JSX.Element {
  const { active } = useCookbook();
  const navigate = useNavigate();
  const [feed, setFeed] = useState<FeedState>({ status: "loading" });
  // "+" quick-add (Ukeplanlegger, Slice 3, mobile card action) — the recipe
  // currently targeted by the add-to-calendar sheet, or null when closed.
  const [quickAddRecipe, setQuickAddRecipe] = useState<RecipeSummary | null>(null);
  // Shopping-list overlay (Ukeplanlegger, Slice 5) — the week (Monday,
  // `YYYY-MM-DD`) it's open for, or null when closed. Set by
  // `MealPlannerPanel`'s "Lag handleliste" button via `onCreateShoppingList`.
  const [shoppingListWeekStart, setShoppingListWeekStart] = useState<string | null>(null);

  // Feed → planner drag-and-drop (Ukeplanlegger, Slice 4). This DndContext
  // spans BOTH the feed grid (drag source, `RecipeCard`) and `MealPlannerPanel`
  // (drop targets) — they live in different subtrees below, so one shared
  // context has to sit above both (see the `return` at the bottom of this
  // component). `SectionsEditor`'s own DndContexts are on a different page
  // entirely and are untouched.
  const mealPlannerRef = useRef<MealPlannerPanelHandle>(null);
  const [draggingRecipe, setDraggingRecipe] = useState<MealPlanDropRecipe | null>(null);
  const dragSensors = useSensors(
    // A small distance so a plain click/tap on the grip doesn't immediately
    // read as a drag — mirrors `SectionsEditor`'s activation constraint.
    // Note: pointer dragging only ever STARTS from the card's dedicated grip
    // handle (`RecipeCard`'s `setActivatorNodeRef`), never from the card body
    // itself — so a click on the title/thumb/link is never at risk of being
    // swallowed by the drag sensor in the first place; there is nothing to
    // suppress there. On mobile, the grip is hidden entirely via CSS below
    // the planner's own desktop breakpoint (1180px, `.recipe-card-drag-handle`),
    // so this PointerSensor (which also carries touch pointer events) never
    // gets a node to attach to on a phone/tablet — no separate TouchSensor is
    // used, and none is needed.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  function handleDragStart(event: DragStartEvent): void {
    const recipe = (event.active.data.current as { recipe?: MealPlanDropRecipe } | undefined)
      ?.recipe;
    setDraggingRecipe(recipe ?? null);
  }

  function handleDragEnd(event: DragEndEvent): void {
    setDraggingRecipe(null);
    // Defense in depth — the UI never gives a read/comment member a drag
    // handle to begin with (see `RecipeCard`'s `planDraggable` gating below),
    // so this should be unreachable; the server (+ RLS) stays authoritative
    // regardless. Mirrors `MealPlannerPanel.handleAddFromDrop`'s own
    // `canEdit` guard.
    if (!canWrite) return;
    const plan = resolveMealPlanDrop(
      event.active ? String(event.active.id) : null,
      event.over ? String(event.over.id) : null,
    );
    const recipe = (event.active.data.current as { recipe?: MealPlanDropRecipe } | undefined)
      ?.recipe;
    if (!plan || !recipe) return;
    mealPlannerRef.current?.addFromDrop(recipe, plan.plannedDate);
  }

  function handleDragCancel(): void {
    setDraggingRecipe(null);
  }

  // Sheets (issue #3): "Ny oppskrift" and "Filter". The refs are the trigger
  // buttons, so focus returns to them on close.
  const [newSheetOpen, setNewSheetOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const newRecipeBtnRef = useRef<HTMLButtonElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);

  // Instant filter (client-side, over the fetched list).
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  // "Til gjennomgang" (pdf-import, Slice 3b) — an additional AND-filter,
  // showing only recipes flagged `reviewState: 'needs_review'`. A finished
  // pdf import auto-activates it (see `handlePdfImported`) so the user lands
  // straight on the recipes it just persisted.
  const [reviewOnly, setReviewOnly] = useState(false);

  /**
   * One-click "approve from the feed" (`RecipeCard`'s × on the "Til
   * gjennomgang" badge) — ids the user has approved THIS SESSION, optimistic.
   * Deliberately NOT folded into `feed.recipes` itself: the underlying
   * `reviewState` is left untouched so the "Til gjennomgang" filter's own
   * predicate (which reads `r.reviewState` directly, see `filtered` below)
   * keeps matching the just-approved card — an explicit product decision so a
   * mis-click doesn't make the card vanish out from under the cursor. The
   * card only actually drops out of the filtered view on the next
   * fetch/navigation (a real refetch clears this set and reflects the
   * server's `reviewState: null`). `RecipeCard` itself is given an
   * OVERRIDDEN recipe (`reviewState` forced to `null` for an approved id) so
   * the badge/× disappear immediately regardless of this filter behaviour.
   */
  const [approvedRecipeIds, setApprovedRecipeIds] = useState<Set<string>>(new Set());
  const [reviewMutationError, setReviewMutationError] = useState<string | null>(null);

  // Fetch whenever the active cookbook (or token) changes. Reset filters too.
  useEffect(() => {
    let ignore = false;
    setFeed({ status: "loading" });
    setSearch("");
    setCategory(null);
    setTags([]);
    setReviewOnly(false);
    setApprovedRecipeIds(new Set());
    setReviewMutationError(null);
    // A sheet belongs to the cookbook it was opened for.
    setNewSheetOpen(false);
    setFilterSheetOpen(false);

    fetchRecipes(active.id, accessToken)
      .then((recipes) => {
        if (!ignore) setFeed({ status: "ok", recipes });
      })
      .catch((err: unknown) => {
        if (ignore) return;
        if (err instanceof UnauthorizedError) {
          setFeed({ status: "unauthorized" });
        } else {
          setFeed({
            status: "error",
            message: err instanceof Error ? err.message : "Ukjent feil",
          });
        }
      });

    return () => {
      ignore = true;
    };
  }, [active.id, accessToken]);

  const recipes = feed.status === "ok" ? feed.recipes : [];

  const categories = useMemo(
    () => [...new Set(recipes.map((r) => r.category).filter((c): c is string => !!c))].sort(),
    [recipes],
  );
  const allTags = useMemo(() => [...new Set(recipes.flatMap((r) => r.tags))].sort(), [recipes]);

  /** Count of `needs_review` recipes in the FULL fetched list (not the
   * filtered one) — the chip's badge count never shrinks just because other
   * filters are also active. Approved-this-session ids are excluded so the
   * count decrements as the user approves, even though the cards themselves
   * stay put (see `approvedRecipeIds`'s docs). */
  const reviewCount = useMemo(
    () =>
      recipes.filter((r) => r.reviewState === "needs_review" && !approvedRecipeIds.has(r.id))
        .length,
    [recipes, approvedRecipeIds],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return recipes
      .filter((r) => {
        const matchesSearch =
          !q || r.title.toLowerCase().includes(q) || (r.intro?.toLowerCase().includes(q) ?? false);
        const matchesCategory = category == null || r.category === category;
        const matchesTags = tags.every((t) => r.tags.includes(t));
        // Reads the recipe's ORIGINAL (un-overridden) `reviewState` (not
        // `approvedRecipeIds`-adjusted) so a card approved THIS SESSION stays
        // matched — it only drops out of the "Til gjennomgang" view on the
        // next fetch/navigation, per `approvedRecipeIds`'s docs above.
        const matchesReview =
          !reviewOnly || r.reviewState === "needs_review" || approvedRecipeIds.has(r.id);
        return matchesSearch && matchesCategory && matchesTags && matchesReview;
      })
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [recipes, search, category, tags, reviewOnly, approvedRecipeIds]);

  function toggleTag(tag: string): void {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  }

  function clearFilters(): void {
    setSearch("");
    setCategory(null);
    setTags([]);
    setReviewOnly(false);
  }

  /** The sheet's "Nullstill" — the filters only; the search box is separate. */
  function resetFilters(): void {
    setCategory(null);
    setTags([]);
    setReviewOnly(false);
  }

  const canWrite = roleCanEditRecipes(active.role);

  /**
   * A photo import (Steg 5) never persists anything itself — it hands back an
   * unsaved `RecipeImportDraft` which we carry into the editor via router
   * state so `RecipeEditorPage` can seed the form for review (see its
   * REVIEW-BEFORE-SAVE docs).
   */
  function handleImported(draft: RecipeImportDraft): void {
    navigate("/recipes/new", { state: { importDraft: draft } });
  }

  /**
   * A pdf import (pdf-import, Slice 3b) PERSISTS every recipe it finds
   * directly into the cookbook (never a draft to review-before-save) — so
   * unlike `handleImported` above, there's nothing to navigate to. Instead:
   * refetch the feed so the newly-imported (needs_review) recipes appear, and
   * auto-activate the "Til gjennomgang" filter so the user lands straight on
   * them.
   */
  function handlePdfImported(): void {
    setReviewOnly(true);
    fetchRecipes(active.id, accessToken)
      .then((fetched) => setFeed({ status: "ok", recipes: fetched }))
      .catch(() => {
        // Best-effort refresh — the user already saw the success message in
        // the modal; a failed refetch just means they'll see the imports on
        // the next natural reload rather than instantly.
      });
  }

  /**
   * `RecipeCard` receives THIS overridden view, not the raw feed entry — an
   * approved-this-session id gets its `reviewState` forced to `null` so the
   * badge/× disappear immediately, independent of `filtered`'s own predicate
   * (which deliberately reads the UN-overridden `reviewState`, see above, so
   * the card itself keeps its place in the "Til gjennomgang" view).
   */
  function displayRecipe(r: RecipeSummary): RecipeSummary {
    return approvedRecipeIds.has(r.id) ? { ...r, reviewState: null } : r;
  }

  /**
   * One-click "approve from the feed" (`RecipeCard`'s ×). Optimistic — adds
   * the id to `approvedRecipeIds` immediately (badge/× disappear, review
   * count decrements), same optimistic/rollback/`onSessionExpired` shape as
   * `MealPlannerPanel`'s `handleServings`/`handleRemove`/`handleAddFromDrop`.
   * `canWrite` is a defence-in-depth guard — the UI never wires `onApproveReview`
   * into `RecipeCard` for a read/comment member in the first place, so the ×
   * doesn't even render for them (mirrors `handleDragEnd`'s own `canWrite`
   * check above).
   */
  function handleApproveReview(recipe: RecipeSummary): void {
    if (!canWrite) return;
    setReviewMutationError(null);
    setApprovedRecipeIds((prev) => new Set(prev).add(recipe.id));

    markRecipeReviewed(recipe.id, accessToken).catch((err: unknown) => {
      setApprovedRecipeIds((prev) => {
        const next = new Set(prev);
        next.delete(recipe.id);
        return next;
      });
      if (err instanceof UnauthorizedError) {
        onSessionExpired();
        return;
      }
      setReviewMutationError("Kunne ikke merke oppskriften som gjennomgått. Prøv igjen.");
    });
  }

  function reloginBlock(testId: string): React.JSX.Element {
    return (
      <div className="feed-state feed-error" data-testid={testId}>
        <h2>Økten er utløpt</h2>
        <p>Du må logge inn på nytt.</p>
        <button
          type="button"
          className="btn btn-primary"
          style={{ marginTop: "16px" }}
          data-testid="feed-relogin"
          onClick={onSessionExpired}
        >
          Logg inn igjen
        </button>
      </div>
    );
  }

  /** The feed (instant filter results). */
  function renderFeed(): React.JSX.Element {
    if (feed.status === "loading") {
      return (
        <div className="feed-state" data-testid="feed-loading">
          <p>Laster oppskrifter …</p>
        </div>
      );
    }
    if (feed.status === "unauthorized") return reloginBlock("feed-unauthorized");
    if (feed.status === "error") {
      return (
        <div className="feed-state feed-error" data-testid="feed-error">
          <h2>Kunne ikke laste oppskrifter</h2>
          <p>{feed.message}</p>
        </div>
      );
    }

    if (recipes.length === 0) {
      return (
        <EmptyState
          testId="feed-empty"
          icon={<BookIcon />}
          title="Ingen oppskrifter ennå"
          message={
            canWrite
              ? `«${active.name}» er tom. Lag den første oppskriften din.`
              : `«${active.name}» er tom. Oppskrifter dukker opp her etter hvert.`
          }
          action={
            canWrite ? (
              <div className="empty-actions">
                <Link to="/recipes/new" className="btn btn-primary" data-testid="empty-new-recipe">
                  ＋ Lag din første oppskrift
                </Link>
              </div>
            ) : undefined
          }
        />
      );
    }

    const [featured, ...rest] = filtered;
    return (
      <section className="feed-content">
        {reviewMutationError && (
          <p className="feed-mutation-error" data-testid="review-mutation-error" role="alert">
            {reviewMutationError}
          </p>
        )}
        {filtered.length === 0 ? (
          <EmptyState
            testId="feed-no-matches"
            icon={<NoMatchIcon />}
            title="Ingen treff"
            message="Ingen oppskrifter passer filtrene dine."
            action={
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="clear-filters"
                onClick={clearFilters}
              >
                Nullstill filtre
              </button>
            }
          />
        ) : (
          <div className="recipe-grid" data-testid="recipe-grid">
            <RecipeCard
              recipe={displayRecipe(featured!)}
              featured
              onQuickAdd={canWrite ? setQuickAddRecipe : undefined}
              planDraggable={canWrite}
              onApproveReview={canWrite ? handleApproveReview : undefined}
            />
            {rest.map((r) => (
              <RecipeCard
                key={r.id}
                recipe={displayRecipe(r)}
                onQuickAdd={canWrite ? setQuickAddRecipe : undefined}
                planDraggable={canWrite}
                onApproveReview={canWrite ? handleApproveReview : undefined}
              />
            ))}
          </div>
        )}
      </section>
    );
  }

  const showFilters = feed.status === "ok" && recipes.length > 0;
  const activeFilterCount = (category != null ? 1 : 0) + tags.length + (reviewOnly ? 1 : 0);

  return (
    // Feed → planner drag-and-drop (Ukeplanlegger, Slice 4): ONE DndContext
    // spans the feed grid (`RecipeCard`s, the drag source) AND
    // `MealPlannerPanel` (the day drop targets) below — they're siblings in
    // this tree, so the shared context has to sit above both. Distinct from
    // — and never touching — `SectionsEditor`'s own DndContexts on the
    // recipe-editor page.
    <DndContext
      sensors={dragSensors}
      collisionDetection={pointerWithinOrClosestCenter}
      modifiers={[snapDragPreviewToCursor]}
      accessibility={PLAN_DRAG_A11Y}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <main className="app-main">
        <div className="feed-toolbar" data-testid="feed-toolbar">
          {canWrite && (
            <button
              ref={newRecipeBtnRef}
              type="button"
              className="btn btn-primary"
              data-testid="new-recipe"
              aria-haspopup="dialog"
              onClick={() => setNewSheetOpen(true)}
            >
              ＋ Ny oppskrift
            </button>
          )}
          {showFilters && (
            <div className="feed-search feed-search-inline">
              <span className="ic">
                <SearchIcon />
              </span>
              <input
                type="search"
                placeholder={`Søk i ${recipes.length} ${recipes.length === 1 ? "oppskrift" : "oppskrifter"} …`}
                aria-label="Søk i oppskrifter"
                data-testid="feed-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )}
        </div>

        {showFilters && (
          <div className="filter-row" data-testid="filter-row">
            <button
              ref={filterBtnRef}
              type="button"
              className="fbtn"
              data-testid="filter-button"
              aria-haspopup="dialog"
              onClick={() => setFilterSheetOpen(true)}
            >
              <FilterIcon />
              Filter
              {activeFilterCount > 0 && (
                <span className="fbtn-badge" data-testid="filter-count">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {category != null && (
              <ActiveChip
                label={category}
                testId="active-category"
                onRemove={() => setCategory(null)}
              />
            )}
            {tags.map((t) => (
              <ActiveChip
                key={t}
                label={t}
                testId={`active-tag-${t}`}
                onRemove={() => toggleTag(t)}
              />
            ))}
            {reviewOnly && (
              <ActiveChip
                label="Til gjennomgang"
                testId="active-review"
                onRemove={() => setReviewOnly(false)}
              />
            )}
          </div>
        )}

        {renderFeed()}
      </main>

      {/* Ukeplanlegger (Slice 3) — desktop right sidebar / mobile floating
          button + overlay. Docks under the sticky TopBar via z-index, not
          document flow, so it never reflows the feed grid above. `ref`
          exposes `addFromDrop` (Slice 4) to `handleDragEnd` above. */}
      <MealPlannerPanel
        ref={mealPlannerRef}
        cookbookId={active.id}
        role={active.role}
        accessToken={accessToken}
        userId={userId}
        onSessionExpired={onSessionExpired}
        onCreateShoppingList={setShoppingListWeekStart}
      />

      {/* No `modifiers` here on purpose: `snapDragPreviewToCursor` is set on
          the enclosing `DndContext` above, and `DragOverlay` — when it has
          no `modifiers` of its own — paints using that already-corrected
          transform via dnd-kit's `ActiveDraggableContext`. Adding it again
          here would double-apply the cursor-snap offset. See
          `pointerWithinOrClosestCenter`'s doc comment in `mealPlanDrag.ts`
          for why collision detection and the visual preview now share this
          one computation instead of drifting apart. */}
      <DragOverlay>
        {draggingRecipe ? <RecipeDragPreview recipe={draggingRecipe} /> : null}
      </DragOverlay>

      {newSheetOpen && canWrite && (
        <NewRecipeSheet
          cookbookId={active.id}
          isPremium={active.isPremium}
          accessToken={accessToken}
          restoreFocusRef={newRecipeBtnRef}
          onClose={() => setNewSheetOpen(false)}
          onSessionExpired={onSessionExpired}
          onImported={handleImported}
          onPdfImported={handlePdfImported}
        />
      )}

      {filterSheetOpen && (
        <FilterSheet
          categories={categories}
          allTags={allTags}
          category={category}
          tags={tags}
          reviewOnly={reviewOnly}
          reviewCount={reviewCount}
          onCategory={setCategory}
          onToggleTag={toggleTag}
          onReviewOnly={setReviewOnly}
          onReset={resetFilters}
          restoreFocusRef={filterBtnRef}
          onClose={() => setFilterSheetOpen(false)}
        />
      )}

      {quickAddRecipe && (
        <AddToCalendarDialog
          cookbookId={active.id}
          recipeId={quickAddRecipe.id}
          recipeTitle={quickAddRecipe.title}
          baseServings={quickAddRecipe.servings}
          accessToken={accessToken}
          onClose={() => setQuickAddRecipe(null)}
          onSessionExpired={onSessionExpired}
          onAdded={() => {
            setQuickAddRecipe(null);
            // The dialog POSTs the meal itself, so the panel knows nothing
            // about it — without this it keeps showing the week it last
            // fetched and the meal looks like it was never added.
            mealPlannerRef.current?.refresh();
          }}
        />
      )}

      {shoppingListWeekStart && (
        <ShoppingListOverlay
          cookbookId={active.id}
          weekStart={shoppingListWeekStart}
          role={active.role}
          accessToken={accessToken}
          onClose={() => setShoppingListWeekStart(null)}
          onSessionExpired={onSessionExpired}
        />
      )}
    </DndContext>
  );
}
